import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import theme from '../../common/color/theme';
import { CenterLayout, FrameLayout, HorizontalEqualLayout, useResponsivePadding, VerticalLayout } from '../../common/common-layout';
import { Body2, H3, H7, Point1 } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import { BTOutlineButton } from '../../common/components/BTOutlineButton';
import { QualificationItem } from '../../common/data/QualificationData';
import { fetchPatents, fetchTabManagers } from '../../common/firebase/FirestoreService';
import i18n from '../../common/i18n/i18n';
import { pxToRem } from '../../common/utils/ConvertUtils';
import '../../css/bbpage.css';
import { useDeviceType } from '../../hooks/useDeviceType';

const ITEMS_PER_PAGE = 12;

const QualificationSection2 = () => {
  const { t } = useTranslation();
  const { isMobile } = useDeviceType();
  const { horizontal: padding } = useResponsivePadding();
  const isEng = ['en', 'en-mobile'].includes(i18n.language);
  const isChina = ['zh', 'zh-mobile'].includes(i18n.language);

  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [visibleCount2, setVisibleCount2] = useState(ITEMS_PER_PAGE);
  const [tabPosition, setTabPosition] = useState(0);
  const [qualificationData, setQualificationData] = useState<QualificationItem[]>([]);
  const [qualificationData2, setQualificationData2] = useState<QualificationItem[]>([]);

  const handleShowMore = () => {
    setVisibleCount(prev => prev + ITEMS_PER_PAGE);
  };

  const handleShowMore2 = () => {
    setVisibleCount2(prev => prev + ITEMS_PER_PAGE);
  };

  const visibleItems = useMemo(() => {
    return qualificationData.length > 0 ? qualificationData.slice(0, visibleCount) : [];
  }, [qualificationData, visibleCount]);

  const hasMore = useMemo(() => {
    return qualificationData.length > 0 && visibleCount < qualificationData.length;
  }, [qualificationData, visibleCount]);

  const visibleItems2 = useMemo(() => {
    return qualificationData2.length > 0 ? qualificationData2.slice(0, visibleCount2) : [];
  }, [qualificationData2, visibleCount2]);

  const hasMore2 = useMemo(() => {
    return qualificationData2.length > 0 && visibleCount2 < qualificationData2.length;
  }, [qualificationData2, visibleCount2]);

  type QualificationItemProps = {
    imageSrc: string;
    type: string;
    title: string;
  };

  useEffect(() => {
    const fetchData = async () => {
      const tab = await fetchTabManagers('patent');
      const tabCodes = tab.map(item => item.tabCode);
      const patents = await fetchPatents(tabCodes);
      const allData = patents.map((patent) => ({
        type: tab.filter((item) => patent.patentType === item.tabCode)[0].tabName,
        title: isEng ? patent.englishTitle : isChina ? patent.chineseTitle : patent.title,
        imageSrc: patent.representImage,
      }))
      const data1 = allData.filter((patent) => patent.type === '인증' || patent.type === '면허');
      const data2 = allData.filter((patent) => patent.type !== '인증' && patent.type !== '면허');

      setQualificationData(data1);
      setQualificationData2(data2);

      if (data2.length === 0 && data1.length > 0) {
        setTabPosition(1);
      }
    };

    fetchData();
  }, []);

  const createQualificationItem = ({ imageSrc, type, title }: QualificationItemProps) => (
    <VerticalLayout >
      <img src={imageSrc} style={{ width: '100%', aspectRatio: '345 / 477', border: '1px solid #979797', boxSizing: 'border-box', }} />
      <BTBox height={isMobile ? 8 : 20} />
      <Point1 style={{ color: theme.colors.BIBI,
        ...isMobile ? {fontWeight: 700} : {}
       }}><Trans i18nKey={`${type}_타입`} /></Point1>
      <BTBox height={isMobile ? 2 : 10} />
      <Body2 style={{ minHeight: isMobile ? 0 : pxToRem(80), wordBreak: 'keep-all',
        ... isMobile ? {fontSize: '13px', lineHeight: '18px' } : {}

       }}>{title}</Body2>
      <BTBox height={ isMobile ? 30 : pxToRem(50)} />
    </VerticalLayout>
  );

  return (
    <VerticalLayout paddingLeft={padding} paddingRight={padding} style={{ background: 'white' }}>
      <BTBox height={pxToRem(108)} />
      <H3>{t('기술로 증명한 품질과 신뢰')}</H3>
      <BTBox height={pxToRem(124)} />

      <HorizontalEqualLayout gap={isMobile ? 16 : 40}>
        <VerticalLayout onClick={() => setTabPosition(0)} style={{ cursor: 'pointer' }} data-analytics-name="탭_인증면허">
          <CenterLayout>
            <H7 style={{ opacity: tabPosition == 0 ? 1.0 : 0.3 }}><Trans i18nKey='인증 / 면허'/></H7>
          </CenterLayout>
          <BTBox height={pxToRem(26)} />
          <VerticalLayout height={pxToRem(4)} backgroundColor='black' style={{ opacity: tabPosition == 0 ? 1.0 : 0.3 }} />
        </VerticalLayout>
        <VerticalLayout onClick={() => setTabPosition(1)} style={{ cursor: 'pointer' }} data-analytics-name="탭_특허">
          <CenterLayout>
            <H7 style={{ opacity: tabPosition == 1 ? 1.0 : 0.3 }}><Trans i18nKey='특허'></Trans></H7>
          </CenterLayout>
          <BTBox height={pxToRem(26)} />
          <VerticalLayout height={pxToRem(4)} backgroundColor='black' style={{ opacity: tabPosition == 1 ? 1.0 : 0.3 }} />
        </VerticalLayout>
      </HorizontalEqualLayout>
      <BTBox height={isMobile ? 12 : pxToRem(67)} />

      <FrameLayout>
        <VerticalLayout style={{ display: tabPosition == 0 ? 'block' : 'none' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(3, minmax(0, 1fr))' : 'repeat(4, 1fr)',
              rowGap: isMobile ? '16px' : '40px',
              columnGap: isMobile ? '16px' : '40px', // 가로 간격도 필요하면 추가
              width: '100%',
            }}
          >
            {visibleItems.map(createQualificationItem)}
          </div>
          
          {hasMore && (
            <>
              <BTBox height={isMobile ? '50px' : `${pxToRem(150)}`} />
              <CenterLayout>
                <div>
                  <BTOutlineButton
                    color="#464646"
                    borderColor="#979797"
                    centered={true}
                    onClick={handleShowMore}
                    hoverBackgroundColor={theme.colors.BIBI}
                    hoverTextDecoration='none'
                    hoverBorderColor='white'
                    hoverColor='white'
                    borderWidth={1}
                    width={`${pxToRem(335)}`}
                    height={`${pxToRem(102)}`}
                    fontSize={`${pxToRem(28)}`}
                    data-analytics-name="더보기_인증면허"
                  >
                    더보기
                  </BTOutlineButton>
                </div>
              </CenterLayout>
            </>
          )}
        </VerticalLayout>
        <VerticalLayout style={{ display: tabPosition == 1 ? 'block' : 'none' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(3, minmax(0, 1fr))' : 'repeat(4, 1fr)',
              rowGap: isMobile ? '16px' : '40px',
              columnGap: isMobile ? '16px' : '40px', // 가로 간격도 필요하면 추가
              width: '100%',
            }}
          >
            {visibleItems2.map(createQualificationItem)}
          </div>

          {hasMore2 && (
            <>
              <BTBox height={isMobile ? '50px' : `${pxToRem(150)}`} />
              <BTBox height={`${pxToRem(150)}`} />
              <CenterLayout>
                <div>
                  <BTOutlineButton
                    color="#464646"
                    borderColor="#979797"
                    centered={true}
                    onClick={handleShowMore2}
                    hoverBackgroundColor={theme.colors.BIBI}
                    hoverTextDecoration='none'
                    hoverBorderColor='white'
                    hoverColor='white'
                    borderWidth={1}
                    width={`${pxToRem(335)}`}
                    height={`${pxToRem(102)}`}
                    fontSize={`${pxToRem(28)}`}
                    data-analytics-name="더보기_특허"
                  >
                    더보기
                  </BTOutlineButton>
                </div>
              </CenterLayout>
            </>
          )}
        </VerticalLayout>
      </FrameLayout>
      <BTBox height={`${pxToRem(183)}`} />
    </VerticalLayout>
  );
};

export default QualificationSection2;
